import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, Award, LogOut, User as UserIcon, Mail, Target, Zap, Trophy, MapPin, Clock, ShieldCheck, Eye, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, token, refreshUser } = useContext(AuthContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [requestsLoadingMine, setRequestsLoadingMine] = useState(false);
  const [requestsFilter, setRequestsFilter] = useState('open'); // open | fulfilled | expired
  const [requestCounts, setRequestCounts] = useState({ open: 0, fulfilled: 0, expired: 0 });
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editProductName, setEditProductName] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [savingRequest, setSavingRequest] = useState(false);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/insights/leaderboard?period=week&limit=5`);
      const data = await response.json();
      setLeaderboard(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch leaderboard', error);
    }
  };

  const fetchMyReports = async () => {
    if (!token) return;
    setReportsLoading(true);
    try {
      const response = await fetch(`${API}/reports/my-reports?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMyReports(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch my reports', error);
    } finally {
      setReportsLoaded(true);
      setReportsLoading(false);
    }
  };

  const fetchMyRequests = async () => {
    if (!token) return;
    setRequestsLoadingMine(true);
    try {
      const response = await fetch(`${API}/requests/mine?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setMyRequests(list);

      const counts = { open: 0, fulfilled: 0, expired: 0 };
      list.forEach((r) => {
        const st = r?.status || 'open';
        if (st === 'open') counts.open += 1;
        else if (st === 'fulfilled') counts.fulfilled += 1;
        else if (st === 'expired') counts.expired += 1;
      });
      setRequestCounts(counts);

      // Lightweight "notification" (MVP): toast once for newly-fulfilled requests.
      const seenKey = 'gw_seen_fulfilled_request_ids';
      const seenRaw = localStorage.getItem(seenKey) || '[]';
      let seen = [];
      try {
        seen = JSON.parse(seenRaw);
        if (!Array.isArray(seen)) seen = [];
      } catch {
        seen = [];
      }
      const newlyFulfilled = list
        .filter((r) => (r?.status || 'open') === 'fulfilled')
        .filter((r) => r?.id && !seen.includes(r.id));
      if (newlyFulfilled.length > 0) {
        toast.success(
          newlyFulfilled.length === 1
            ? `We found what you were looking for: ${newlyFulfilled[0].product_name}`
            : `We found ${newlyFulfilled.length} requested products`
        );
        const nextSeen = Array.from(new Set([...seen, ...newlyFulfilled.map((r) => r.id)]));
        localStorage.setItem(seenKey, JSON.stringify(nextSeen));
      }

      // If user has no open requests, auto-switch to the most relevant tab.
      if (counts.open === 0 && counts.fulfilled > 0) setRequestsFilter('fulfilled');
    } catch (error) {
      console.error('Failed to fetch my requests', error);
    } finally {
      setRequestsLoaded(true);
      setRequestsLoadingMine(false);
    }
  };

  const startEditRequest = (r) => {
    setEditingRequestId(r.id);
    setEditProductName(r.product_name || '');
    setEditDetails(r.details || '');
  };

  const cancelEditRequest = () => {
    setEditingRequestId(null);
    setEditProductName('');
    setEditDetails('');
  };

  const saveRequestEdit = async () => {
    if (!editingRequestId) return;
    const product_name = editProductName.trim();
    if (!product_name) {
      toast.error('Product name cannot be empty');
      return;
    }
    setSavingRequest(true);
    try {
      const response = await fetch(`${API}/requests/${editingRequestId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_name,
          details: editDetails.trim() || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Could not update request');
        return;
      }
      toast.success('Request updated');
      cancelEditRequest();
      fetchMyRequests();
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSavingRequest(false);
    }
  };

  const requestAgain = async (r) => {
    if (!r?.product_name) return;
    setSavingRequest(true);
    try {
      const response = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_name: r.product_name,
          details: r.details || null,
          city: r.city,
          lat: r.lat ?? null,
          lng: r.lng ?? null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Could not create request');
        return;
      }
      toast.success('Requested again');
      setRequestsFilter('open');
      fetchMyRequests();
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSavingRequest(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/auth');
    } else {
      refreshUser();
      fetchLeaderboard();
    }
    // We intentionally call the fetch helpers directly and only when token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, refreshUser]);

  useEffect(() => {
    // If a token exists but the user never loads (expired/invalid token),
    // quickly redirect to sign-in instead of showing a blank/loading screen.
    if (!token || user) return;
    const t = window.setTimeout(() => {
      navigate('/auth');
    }, 800);
    return () => window.clearTimeout(t);
  }, [token, user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button onClick={() => navigate('/search')} variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-primary">Profile</h1>
            <div className="w-10" />
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-10">
          <Card className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
            <p className="mt-4 text-sm text-muted-foreground">
              Checking your session…
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => navigate('/auth')}>
                Sign in
              </Button>
              <Button variant="ghost" className="rounded-full" onClick={() => navigate('/search')}>
                Back to Search
              </Button>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  const POINTS_PER_REPORT = 1;
  const reportsCount = Math.floor(user.points / POINTS_PER_REPORT);

  const milestones = [
    { id: 'first_report', label: 'First price report', done: reportsCount >= 1, icon: Zap },
    { id: '10_reports', label: '10 reports', done: reportsCount >= 10, icon: Target },
  ];

  const filteredRequests = (myRequests || []).filter((r) => (r.status || 'open') === requestsFilter);

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/search')}
            variant="ghost"
            size="icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-heading font-bold text-primary">Profile</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-6 mb-6">
            <Avatar className="w-20 h-20 border-4 border-primary">
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-heading font-bold">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-1">{user.name}</h2>
              <p className="text-muted-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {user.email}
              </p>
              {user.public_name && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">Community name: {user.public_name}</Badge>
                  {user.trusted && <Badge>Trusted</Badge>}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-accent/20 to-primary/10 rounded-2xl p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Award className="w-8 h-8 text-accent" />
              <span className="text-4xl font-heading font-bold text-foreground tabular-nums">
                {user.points}
              </span>
            </div>
            <p className="text-muted-foreground">Total Points Earned</p>
            <p className="text-sm text-muted-foreground mt-1">
              ~{reportsCount} price report{reportsCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="mt-4">
            <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              Milestones
            </h4>
            <div className="flex flex-wrap gap-2">
              {milestones.map((m) => {
                const Icon = m.icon;
                return (
                  <Badge
                    key={m.id}
                    variant={m.done ? 'default' : 'outline'}
                    className="flex items-center gap-1.5 py-1.5"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {m.label}
                    {m.done && ' ✓'}
                  </Badge>
                );
              })}
            </div>
          </div>
        </Card>

        {user?.is_admin && (
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg text-foreground flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  Admin Tools
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Review and moderate submitted price reports.
                </p>
              </div>
              <Button onClick={() => navigate('/admin')} className="rounded-full">
                Dashboard
              </Button>
            </div>
          </Card>
        )}

        {leaderboard.length > 0 && (
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-foreground flex items-center gap-2">
                <Trophy className="w-5 h-5 text-accent" />
                Weekly Leaderboard
              </h3>
              <span className="text-xs text-muted-foreground">Top contributors (all cities)</span>
            </div>
            <div className="space-y-2">
              {leaderboard.map((entry) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${
                    entry.user_id === user.id ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 text-center font-semibold text-muted-foreground">
                      #{entry.rank}
                    </span>
                    <div>
                      <p className="font-medium text-foreground">
                        {entry.user_name}
                        {entry.user_id === user.id && ' (You)'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {entry.report_count} report{entry.report_count === 1 ? '' : 's'} • ~
                        {entry.estimated_points} pts
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {(myReports.length > 0 || !reportsLoaded) && (
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-foreground flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                My recent reports
              </h3>
              <span className="text-xs text-muted-foreground">
                {reportsLoaded ? `Last ${myReports.length} reports` : 'Not loaded'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Pending reports are hidden from search until approved by an admin.
            </p>
            {!reportsLoaded ? (
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  Load your recent reports when you need them.
                </div>
                <Button
                  size="sm"
                  className="rounded-full"
                  onClick={fetchMyReports}
                  disabled={reportsLoading}
                >
                  {reportsLoading ? 'Loading…' : 'Load'}
                </Button>
              </div>
            ) : myReports.length === 0 ? (
              <div className="text-sm text-muted-foreground">No reports yet.</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
              {myReports.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between rounded-xl px-3 py-2 text-sm bg-muted/50"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">
                      {r.product_name}
                      {r.brand && <span className="text-muted-foreground"> • {r.brand}</span>}
                      {r.size && (
                        <span className="text-muted-foreground text-xs"> ({r.size})</span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {r.store_name} • {r.city}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {new Date(r.reported_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-heading font-bold text-primary tabular-nums">
                      R{Number(r.price).toFixed(2)}
                    </div>
                    {r.status === 'pending' && (
                      <div className="mt-1">
                        <Badge variant="secondary" className="text-[10px]">
                          Pending review
                        </Badge>
                      </div>
                    )}
                    {r.status === 'rejected' && (
                      <div className="mt-1">
                        <Badge variant="destructive" className="text-[10px]">
                          Rejected
                        </Badge>
                        {r.moderation_reason && (
                          <div className="text-[11px] text-muted-foreground mt-1 max-w-[220px]">
                            Reason: {r.moderation_reason}
                          </div>
                        )}
                      </div>
                    )}
                    {r.deal_quantity && r.deal_price && (
                      <div className="text-[11px] text-accent">
                        {r.deal_label || `${r.deal_quantity} for R${Number(r.deal_price).toFixed(2)}`}
                      </div>
                    )}
                    {(r.flags_up > 0 || r.flags_down > 0) && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {r.flags_up ?? 0} 👍 · {r.flags_down ?? 0} 👎
                      </div>
                    )}
                  </div>
                </div>
              ))}
              </div>
            )}
          </Card>
        )}

        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg text-foreground flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent" />
              My requests
            </h3>
            <div className="flex flex-wrap justify-end gap-2">
              {['open', 'fulfilled', 'expired'].map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={requestsFilter === s ? 'default' : 'outline'}
                  className="rounded-full px-3"
                  onClick={() => setRequestsFilter(s)}
                >
                  <span className="capitalize">{s}</span>
                  <span className="ml-2 text-[11px] opacity-80 tabular-nums">
                    {requestCounts[s] ?? 0}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          {!requestsLoaded ? (
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-muted-foreground">
                Load your requests when you need them.
              </div>
              <Button
                size="sm"
                className="rounded-full"
                onClick={fetchMyRequests}
                disabled={requestsLoadingMine}
              >
                {requestsLoadingMine ? 'Loading…' : 'Load'}
              </Button>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {requestsFilter === 'open'
                ? 'No open requests. If you can’t find a product, request it from the Search page.'
                : `No ${requestsFilter} requests yet.`}
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {filteredRequests.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start justify-between gap-3 rounded-xl px-3 py-2 text-sm bg-muted/50"
                >
                  <div className="flex-1">
                    {editingRequestId === r.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editProductName}
                          onChange={(e) => setEditProductName(e.target.value)}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                          placeholder="Product name"
                        />
                        <textarea
                          value={editDetails}
                          onChange={(e) => setEditDetails(e.target.value)}
                          className="w-full min-h-[72px] rounded-md border border-border bg-background px-3 py-2 text-sm"
                          placeholder="Details (optional)"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-full"
                            onClick={cancelEditRequest}
                            disabled={savingRequest}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="rounded-full bg-accent hover:bg-accent/90"
                            onClick={saveRequestEdit}
                            disabled={savingRequest}
                          >
                            {savingRequest ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="font-medium text-foreground">{r.product_name}</div>
                        {r.details && <div className="text-xs text-muted-foreground mt-1">{r.details}</div>}
                      </>
                    )}
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {r.city}
                      <span className="mx-1">•</span>
                      <Clock className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                    {r.status === 'fulfilled' && r.fulfilled_at && (
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-accent" />
                        Fulfilled {new Date(r.fulfilled_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant={r.status === 'fulfilled' ? 'default' : r.status === 'expired' ? 'secondary' : 'outline'}>
                      {r.status || 'open'}
                    </Badge>
                    {editingRequestId !== r.id && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => navigate(`/results?q=${encodeURIComponent(r.product_name || '')}`)}
                        >
                          Search
                        </Button>
                        {r.status === 'open' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-full"
                            onClick={() => startEditRequest(r)}
                            disabled={savingRequest}
                          >
                            Edit
                          </Button>
                        )}
                        {(r.status === 'expired' || r.status === 'fulfilled') && (
                          <Button
                            size="sm"
                            className="rounded-full bg-accent hover:bg-accent/90"
                            onClick={() => requestAgain(r)}
                            disabled={savingRequest}
                          >
                            Request again
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 mb-6">
          <h3 className="font-heading font-bold text-lg text-foreground mb-4">Account Details</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
              <UserIcon className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium text-foreground">{user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
              <Mail className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium text-foreground">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
              <Award className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Contributor Status</p>
                <Badge variant="secondary" className="mt-1">
                  {user.points >= 100
                    ? 'Gold Contributor'
                    : user.points >= 50
                    ? 'Silver Contributor'
                    : 'Community Member'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 mb-6">
          <h3 className="font-heading font-bold text-lg text-foreground mb-4">Help</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate('/report-issue')}
            >
              Report an Issue
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <a href="mailto:support@vexi.co.za">Contact / Feedback</a>
            </Button>
          </div>
        </Card>

        <div className="mt-6">
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="destructive"
            className="w-full rounded-full"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>
      </main>
    </div>
  );
}